import { type ReactNode } from 'react';
import {
  // standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  ArrowRight,
  Check,
  ChevronRight,
  Files,
  GraduationCap,
  Layers3,
  Minus,
  ScanText,
  Scissors,
  ShieldCheck,
  Table2,
} from 'lucide-react';
import type {
  LearningAccelerationReport,
  LearningEligibility,
  LearningSessionSummary,
  LearningStatus,
} from '../shared/desktop-api';
import { TooltipButton } from './TooltipButton';
import { MilestoneArt } from './LearningDestination';
import {
  hasAvailableLearningPattern,
  hasMeasuredLearningTrial,
  learningNumber as number,
} from './learning-destination-model';
import {
  countSkills,
  SKILL_STATE_LABEL,
  skillTitle,
  type LearningSkill,
  type LearningSkillGroup,
  type SkillCounts,
  type SkillState,
} from '../shared/learning-skills';

/** One engine milestone, shown as a checkpoint inside its stage. `value` and `progress` are native counts. */
export type JourneyMilestone = {
  name: string;
  state: string;
  done: boolean;
  detail: string;
  tooltip: string;
  value?: string;
  progress?: number;
};
/** Teach, then Train, then Results. Skills are used as soon as they are Ready; Results is what training changed. */
export const JOURNEY_CHAPTERS = ['Teach', 'Train', 'Results'] as const;
/** Above this many skills, a row shows one bar per document type instead of a mark per skill. */
export const COMPACT_SKILLS = 60;
/** Up to this many skills, Train shows each as a named tile instead of a mark. */
export const TILE_SKILLS = 12;

const plural = (count: number, one: string, many = `${one}s`) =>
  `${number(count)} ${count === 1 ? one : many}`;

/** The six milestones, two per stage: Teach (0–1), Train (2–3) and Results (4–5).
 * Receipts, not predictions: every state here restates a native count. */
export function journeyMilestones({
  report,
  eligibility,
  loading,
  sessions,
  historyUnavailable,
  measuredTrials,
  status,
  counts,
}: {
  report: LearningAccelerationReport | null;
  eligibility: LearningEligibility | null;
  loading: boolean;
  sessions: LearningSessionSummary[];
  historyUnavailable: boolean;
  measuredTrials: boolean;
  status: LearningStatus | null;
  counts: SkillCounts;
}): JourneyMilestone[] {
  const available = hasAvailableLearningPattern(report);
  const use = improvementUse(report);
  const used = use.length > 0;
  // An applied, measured improvement is independent proof that something was tested.
  const tested =
    used ||
    measuredTrials ||
    hasMeasuredLearningTrial(status) ||
    sessions.some((session) => hasMeasuredLearningTrial(session.lastCheckpoint));
  const known = counts.total > 0;
  return [
    {
      name: 'First skills',
      state: available
        ? 'Available'
        : report && available === null
          ? 'Still counting'
          : report
            ? 'Building'
            : loading
              ? 'Checking…'
              : 'Not reported',
      done: !!available,
      detail: 'Confirmed examples become skills this job can use.',
      tooltip: '',
    },
    {
      name: 'Broader coverage',
      state: known
        ? `${number(counts.taught)} of ${number(counts.total)} taught`
        : loading
          ? 'Checking…'
          : 'Not reported',
      done: known && counts.todo === 0 && counts.unknown === 0,
      detail: 'Teach every document type, field and table this job reads.',
      tooltip: known
        ? `${number(counts.taught)} of ${number(counts.total)} skills have at least one confirmed example. New types and fields add more to teach.`
        : '',
      value: known ? `${number(counts.taught)}/${number(counts.total)}` : undefined,
      progress: known ? counts.taught / counts.total : undefined,
    },
    {
      name: 'Ready to train',
      state: eligibility ? (eligibility.canStart ? 'Unlocked' : 'More examples') : 'Not checked',
      done: !!eligibility?.canStart,
      detail: 'Training can start once one skill has enough confirmed examples to train on.',
      tooltip: '',
    },
    {
      name: 'Train across the job',
      state: known
        ? `${number(counts.ready)} of ${number(counts.total)} Ready`
        : loading
          ? 'Checking…'
          : 'Not reported',
      done: known && counts.ready === counts.total && counts.unknown === 0,
      detail: 'Every skill has enough confirmed examples.',
      tooltip: known
        ? `${number(counts.ready)} of ${number(counts.total)} skills have enough confirmed examples.`
        : '',
      value: known ? `${number(counts.ready)}/${number(counts.total)}` : undefined,
      progress: known ? counts.ready / counts.total : undefined,
    },
    {
      name: 'First training results',
      state: tested ? 'Tests recorded' : historyUnavailable ? 'Not reported' : 'No tests yet',
      done: tested,
      detail: 'Training checked new ways against your confirmed examples.',
      tooltip: '',
    },
    {
      name: 'Improvements in use',
      state: used ? 'Use recorded' : 'Not reported',
      done: used,
      detail: used
        ? 'Kept changes are working on this job’s documents.'
        : 'No use of kept changes recorded yet.',
      tooltip: use.map((entry) => `${entry.label} ${entry.value}.`).join(' '),
    },
  ];
}

/** Recorded use of kept training changes, by kind of change, on documents the job still holds. */
export function improvementUse(
  report: LearningAccelerationReport | null,
): { kind: 'field' | 'table' | 'type' | 'layout' | 'starts'; label: string; value: string }[] {
  const use =
    report?.improvementUse && [1, 2, 3, 4, 5].includes(report.improvementUse.version)
      ? report.improvementUse
      : null;
  if (!use) return [];
  return [
    use.version >= 2 &&
    (use.fieldExperiments ?? 0) > 0 &&
    (use.fieldDocuments ?? 0) > 0 &&
    (use.fieldValues ?? 0) > 0
      ? {
          kind: 'field' as const,
          label: 'Field changes',
          value: `read ${plural(use.fieldValues!, 'value')} on ${plural(use.fieldDocuments!, 'document')}`,
        }
      : null,
    use.tableExperiments > 0 && use.tableDocuments > 0 && use.tableRows > 0
      ? {
          kind: 'table' as const,
          label: 'Table changes',
          value: `read ${plural(use.tableRows, 'row')} on ${plural(use.tableDocuments, 'document')}`,
        }
      : null,
    use.version >= 3 && (use.typeImprovements ?? 0) > 0 && (use.typeDocuments ?? 0) > 0
      ? {
          kind: 'type' as const,
          label: 'Document type changes',
          value: `recognized ${plural(use.typeDocuments!, 'document')}`,
        }
      : null,
    use.version >= 4 && (use.layoutImprovements ?? 0) > 0 && (use.layoutDocuments ?? 0) > 0
      ? {
          kind: 'layout' as const,
          label: 'Recognition changes',
          value: `recognized ${plural(use.layoutDocuments!, 'document')}`,
        }
      : null,
    use.version === 5 && (use.boundaryImprovements ?? 0) > 0 && (use.boundaryPages ?? 0) > 0
      ? {
          kind: 'starts' as const,
          label: 'Document start changes',
          value: `placed ${plural(use.boundaryPages!, 'page')}`,
        }
      : null,
  ].filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));
}

/** One mark per skill: dashed ring, progress ring, solid dot, or check. */
export function SkillGlyph({
  state,
  have,
  need,
  size = 14,
}: {
  state: SkillState | null;
  have?: number;
  need?: number;
  size?: number;
}) {
  const c = size / 2,
    r = size / 2 - 1.6;
  if (state === 'use')
    return (
      <svg
        className="lj-glyph is-use"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
      >
        <circle cx={c} cy={c} r={c} />
        <path
          d={`M${c - size * 0.22} ${c + 0.2}l${size * 0.15} ${size * 0.15} ${size * 0.29}-${size * 0.32}`}
        />
      </svg>
    );
  if (state === 'learned')
    return (
      <svg
        className="lj-glyph is-learned"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
      >
        <circle cx={c} cy={c} r={c - 0.5} />
      </svg>
    );
  if (state === 'learning') {
    const share = have != null && need != null && have + need > 0 ? have / (have + need) : 0.5,
      length = 2 * Math.PI * r;
    return (
      <svg
        className="lj-glyph is-learning"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
      >
        <circle className="track" cx={c} cy={c} r={r} />
        <circle
          className="arc"
          cx={c}
          cy={c}
          r={r}
          strokeDasharray={`${(length * share).toFixed(2)} ${length.toFixed(2)}`}
          transform={`rotate(-90 ${c} ${c})`}
        />
      </svg>
    );
  }
  return (
    <svg
      className={`lj-glyph ${state === 'todo' ? 'is-todo' : 'is-unknown'}`}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
    >
      <circle cx={c} cy={c} r={r} />
    </svg>
  );
}

/** Not taught, Learning, Ready — and how many Ready skills are already in use. */
export function SkillLegend({ counts }: { counts: SkillCounts }) {
  return (
    <ul className="lj-legend" aria-label="Skill states">
      <li>
        <SkillGlyph state="todo" />
        {SKILL_STATE_LABEL.todo} <b>{number(counts.todo)}</b>
      </li>
      <li>
        <SkillGlyph state="learning" have={1} need={1} />
        {SKILL_STATE_LABEL.learning} <b>{number(counts.learning)}</b>
      </li>
      <li>
        <SkillGlyph state="learned" />
        {SKILL_STATE_LABEL.learned} <b>{number(counts.ready)}</b>
      </li>
      <li className="is-use">
        <SkillGlyph state="use" />
        <b>{number(counts.use)}</b> of them in use
      </li>
    </ul>
  );
}
const STACK: SkillState[] = ['use', 'learned', 'learning', 'todo'];
export function SkillStack({ counts }: { counts: SkillCounts }) {
  return (
    <div className="lj-stack" aria-hidden="true">
      {STACK.map((state) =>
        counts[state] ? (
          <i key={state} className={`is-${state}`} style={{ flex: counts[state] }} />
        ) : null,
      )}
    </div>
  );
}

const stateName = (skill: LearningSkill) =>
  skill.state ? SKILL_STATE_LABEL[skill.state] : skill.counting ? 'Still counting' : 'Not reported';
function SkillMark({
  skill,
  size = 15,
  named = false,
}: {
  skill: LearningSkill;
  size?: number;
  named?: boolean;
}) {
  return (
    <TooltipButton
      type="button"
      className={named ? 'lj-skill-mark lj-skill-chip' : 'lj-skill-mark'}
      tooltip={`${skillTitle(skill)} · ${stateName(skill)}`}
      aria-label={`${skillTitle(skill)}: ${stateName(skill)}. ${skill.detail}`}
    >
      <SkillGlyph state={skill.state} have={skill.have} need={skill.need} size={size} />
      {named && <span aria-hidden="true">{skill.label}</span>}
    </TooltipButton>
  );
}

/** Every document type as a row: a mark per skill (named on a small job), or one bar per type on big jobs. */
export function SkillRows({
  groups,
  compact = false,
  named = false,
}: {
  groups: readonly LearningSkillGroup[];
  compact?: boolean;
  named?: boolean;
}) {
  return (
    <ul className={`lj-skill-rows ${compact ? 'is-compact' : ''}`} aria-label="What this job knows">
      {groups.map((group) => {
        const known = group.skills.filter((skill) => skill.state),
          notTaught = known.filter((skill) => skill.state === 'todo').length;
        // "All taught" only when every skill has a reported state: one still loading or counting may be untaught.
        const unreported = group.skills.length - known.length;
        const own = countSkills([group]);
        return (
          <li key={group.key}>
            {group.kind === 'starts' ? (
              <Scissors size={17} aria-hidden="true" />
            ) : (
              <Files size={17} aria-hidden="true" />
            )}
            <strong title={group.name}>{group.name}</strong>
            {compact ? (
              <span
                className="lj-skill-bar"
                role="img"
                aria-label={`${group.name}: ${plural(own.ready, 'skill')} Ready, ${number(own.learning)} learning, ${number(own.todo)} not taught`}
              >
                <SkillStack counts={own} />
                <small>{plural(group.skills.length, 'skill')}</small>
              </span>
            ) : (
              <span className={named ? 'lj-skill-marks is-named' : 'lj-skill-marks'}>
                {group.skills.map((skill) => (
                  <SkillMark key={skill.key} skill={skill} named={named} />
                ))}
              </span>
            )}
            <em className={known.length && !notTaught && !unreported ? 'is-done' : ''}>
              {!known.length ? (
                'Not reported'
              ) : notTaught ? (
                `${number(notTaught)} not taught`
              ) : unreported ? (
                `${number(unreported)} not reported`
              ) : (
                <>
                  <Check size={13} />
                  All taught
                </>
              )}
            </em>
          </li>
        );
      })}
    </ul>
  );
}

/** What a skill still needs, in words. A count only when the engine's own readiness
 * requirement states it in confirmed examples; otherwise just whether it is Ready. */
export function skillNeeds(skill: LearningSkill) {
  if (skill.state === 'use') return 'Ready · in use';
  if (skill.state === 'learned') return 'Ready';
  if (!skill.state) return skill.counting ? 'Still counting' : 'Not reported';
  return skill.examplesNeeded
    ? `Needs ${plural(skill.examplesNeeded, 'more confirmed example')}`
    : 'Not ready yet';
}

/** A small job's skills as tiles: each by its plain name, Ready or not, and what it still needs. */
export function SkillTiles({ skills }: { skills: readonly LearningSkill[] }) {
  return (
    <ul className="lj-skill-tiles" aria-label="Skills">
      {skills.map((skill) => {
        const ready = skill.state === 'learned' || skill.state === 'use';
        return (
          <li key={skill.key} className={`lj-skill-tile ${ready ? 'is-ready' : ''}`}>
            <SkillGlyph state={skill.state} have={skill.have} need={skill.need} size={18} />
            <span>
              <b>{skill.kind === 'type' ? skillTitle(skill) : skill.label}</b>
              {skill.kind !== 'type' && skill.typeName && <small>{skill.typeName}</small>}
            </span>
            <em>{skillNeeds(skill)}</em>
          </li>
        );
      })}
    </ul>
  );
}

/** Closest to Ready: the engine's remaining requirement as a bar, or its reason when a skill is on hold. */
export function ClosestSkills({ skills }: { skills: readonly LearningSkill[] }) {
  if (!skills.length) return null;
  return (
    <ul className="lj-closest">
      {skills.map((skill) => {
        const total =
          skill.have != null && skill.need != null && skill.have + skill.need > 0
            ? skill.have + skill.need
            : null;
        const name = skill.kind === 'type' ? skillTitle(skill) : skill.label;
        return (
          <li key={skill.key} className="lj-closest-skill">
            <span className="lj-closest-top">
              <b title={name}>{name}</b>
              {skill.need ? <em>{number(skill.need)} more</em> : null}
            </span>
            {total ? (
              <span
                className="lj-closest-track"
                role="progressbar"
                aria-label={`${skillTitle(skill)}: ${number(skill.need!)} more confirmed examples needed`}
                aria-valuemin={0}
                aria-valuemax={total}
                aria-valuenow={skill.have}
              >
                <i style={{ width: `${(skill.have! / total) * 100}%` }} />
              </span>
            ) : (
              <small className="lj-closest-reason">{skill.detail}</small>
            )}
            <small>
              {skill.kind === 'type' ? 'Document type' : (skill.typeName ?? 'Every document')}
            </small>
          </li>
        );
      })}
    </ul>
  );
}

/** The three stages are Learning's navigation; each carries its two milestones as checkpoints. */
export function JourneyStages({
  milestones,
  stage,
  status,
  onSelect,
}: {
  milestones: readonly JourneyMilestone[];
  stage: number;
  status: readonly [string, string, string];
  onSelect: (stage: number) => void;
}) {
  const art = [1, 2, 5];
  return (
    <nav className="lj-journey" aria-label="Your learning journey">
      <ol className="lj-stages">
        {JOURNEY_CHAPTERS.map((name, index) => (
          <li key={name} className="lj-stage-slot">
            {index > 0 && <ArrowRight className="lj-stage-arrow" size={18} aria-hidden="true" />}
            <button
              type="button"
              className={`lj-stage ${index === stage ? 'is-selected' : ''}`}
              aria-pressed={index === stage}
              onClick={() => onSelect(index)}
              data-telemetry={
                index === 0 ? 'learning-teach' : index === 1 ? 'learning-train' : 'learning-results'
              }
            >
              <MilestoneArt kind={art[index]} />
              <span className="lj-stage-text">
                <strong>{name}</strong>
                <em>{status[index]}</em>
                {milestones.slice(index * 2, index * 2 + 2).map((milestone) => (
                  <span
                    key={milestone.name}
                    className={`lj-checkpoint ${milestone.done ? 'is-reached' : ''}`}
                  >
                    {milestone.done && (
                      <i aria-hidden="true">
                        <Check size={9} />
                      </i>
                    )}
                    <span>{milestone.name}</span>
                  </span>
                ))}
              </span>
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function Advanced({
  summary = 'Advanced details',
  children,
  className = '',
}: {
  summary?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <details className={`lj-advanced ${className}`}>
      <summary>
        <ChevronRight size={15} aria-hidden="true" />
        {summary}
      </summary>
      <div className="lj-advanced-body">{children}</div>
    </details>
  );
}

/** Teaching happens on documents, so this leaves Learning for Documents. */
export function TeachButton({ onTeach }: { onTeach: () => void }) {
  return (
    <button type="button" className="primary-button lj-teach-button" onClick={onTeach}>
      <GraduationCap size={17} aria-hidden="true" />
      Teach in Documents
      <ArrowRight size={15} aria-hidden="true" />
    </button>
  );
}

/** The trays' fractions count only skills with a reported state; the rest are named, never folded into them. */
function UnreportedSkills({ counts }: { counts: SkillCounts }) {
  return counts.unknown > 0 ? (
    <p className="lj-quiet">{plural(counts.unknown, 'more skill')} not reported yet.</p>
  ) : null;
}

/**
 * The Calibration switch (owner, 2026-10-30): under Teach in Documents, on for every new job. While on,
 * the documents list asks about more, with no counts; it turns itself off once the job is ready, and the
 * operator can turn it off early or back on (shared/calibration.ts).
 */
export type CalibrationSwitch = {
  on: boolean;
  endedAt?: string | null;
  endedBy?: string | null;
  busy?: boolean;
  onChange: (on: boolean) => void;
  /** On, and paused because the job is on Autopilot (owner, 2026-10-31): it resumes on Manual. */
  paused?: boolean;
};
function CalibrationRow({ calibration }: { calibration: CalibrationSwitch }) {
  const since = calibration.endedAt
    ? new Date(calibration.endedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
    : '';
  const line = calibration.on
    ? calibration.paused
      ? 'Paused while this job is on Autopilot'
      : 'New jobs start with it on'
    : !since
      ? 'Off'
      : calibration.endedBy === 'ready'
        ? `Off since ${since}: this job was ready`
        : `Off since ${since}`;
  return (
    <label className="lj-calibration">
      <input
        type="checkbox"
        role="switch"
        checked={calibration.on}
        disabled={calibration.busy}
        data-telemetry="calibration-switch"
        onChange={(event) => calibration.onChange(event.target.checked)}
      />
      <b>Calibration</b>
      <small>{line}</small>
    </label>
  );
}

export function TeachTray({
  counts,
  groups,
  loading,
  onTeach,
  layoutHold,
  calibration,
}: {
  counts: SkillCounts;
  groups: readonly LearningSkillGroup[];
  loading: boolean;
  onTeach?: () => void;
  /** The engine's own reason confirmed documents are not yet in a learned layout; never a silent gap. */
  layoutHold?: string | null;
  calibration?: CalibrationSwitch;
}) {
  return (
    <div className="lj-tray-grid lj-teach">
      <div className="lj-stat-column">
        <span className="lj-eyebrow">Teach</span>
        {counts.total ? (
          <div className="lj-big">
            <b>
              {number(counts.taught)}
              <small>/{number(counts.total)}</small>
            </b>
            <span>skills taught</span>
          </div>
        ) : (
          <p className="lj-quiet">
            {loading
              ? 'Reading what this job knows…'
              : 'Nothing reported yet. Confirmed documents start the first skills.'}
          </p>
        )}
        {counts.total > 0 && <UnreportedSkills counts={counts} />}
        {counts.total > 0 && (
          <>
            <SkillStack counts={counts} />
            <SkillLegend counts={counts} />
          </>
        )}
        {onTeach && <TeachButton onTeach={onTeach} />}
        {calibration && <CalibrationRow calibration={calibration} />}
      </div>
      <div className="lj-skills-column">
        {groups.length ? (
          <SkillRows
            groups={groups}
            compact={counts.total > COMPACT_SKILLS}
            named={counts.total <= TILE_SKILLS}
          />
        ) : loading ? (
          <p className="lj-quiet">Reading document types…</p>
        ) : (
          <div className="lj-empty">
            <MilestoneArt kind={1} />
            <p className="lj-quiet">Add document types to this job to see what it can learn.</p>
          </div>
        )}
        {layoutHold && (
          <p className="lj-quiet lj-hold-note">
            <Layers3 size={15} aria-hidden="true" />
            <span>{layoutHold}</span>
          </p>
        )}
        {groups.length > 0 && (
          <div className="lj-room" aria-hidden="true">
            <MilestoneArt kind={1} />
          </div>
        )}
      </div>
    </div>
  );
}

export function TrainTray({
  card,
  counts,
  groups,
  closest,
  advanced,
  footer,
}: {
  card: ReactNode;
  counts: SkillCounts;
  groups: readonly LearningSkillGroup[];
  closest: readonly LearningSkill[];
  advanced?: ReactNode;
  footer?: ReactNode;
}) {
  const waiting = counts.total - counts.ready;
  // A small job's skills are tiles that already say what each still needs, so Closest to ready would repeat them.
  const tiles = counts.total > 0 && counts.total <= TILE_SKILLS;
  // The card and the counts side by side; Closest to ready spans the tray below them, so its cards spread across the row.
  return (
    <div className="lj-tray-grid lj-train">
      <div className="lj-train-card-column">{card}</div>
      <div className="lj-stat-column">
        <span className="lj-eyebrow">Train across the job</span>
        {counts.total ? (
          <>
            <div className="lj-big">
              <b>
                {number(counts.ready)}
                <small>/{number(counts.total)}</small>
              </b>
              <span>{counts.ready ? 'skills are Ready' : 'skills are Ready yet'}</span>
            </div>
            <UnreportedSkills counts={counts} />
            <p className="lj-quiet">
              Ready means it has enough confirmed examples.
              {waiting > 0 ? ` The other ${number(waiting)} need more first.` : ''}
            </p>
            {tiles ? (
              <SkillTiles skills={groups.flatMap((group) => group.skills)} />
            ) : counts.total <= COMPACT_SKILLS ? (
              <span className="lj-skill-marks lj-all-marks">
                {groups
                  .flatMap((group) => group.skills)
                  .map((skill) => (
                    <SkillMark key={skill.key} skill={skill} size={16} />
                  ))}
              </span>
            ) : (
              <SkillStack counts={counts} />
            )}
          </>
        ) : (
          <p className="lj-quiet">What this job knows has not been reported yet.</p>
        )}
        <div className="lj-room" aria-hidden="true">
          <MilestoneArt kind={3} />
        </div>
      </div>
      {closest.length > 0 && !tiles && (
        <div className="lj-stat-column lj-train-wide">
          <span className="lj-eyebrow">Closest to ready</span>
          <ClosestSkills skills={closest} />
        </div>
      )}
      {(advanced || footer) && (
        <div className="lj-train-wide lj-train-foot">
          {advanced && <Advanced>{advanced}</Advanced>}
          {footer}
        </div>
      )}
    </div>
  );
}

/** One training run as the engine recorded it: what it kept, and what it didn't keep with its reasons. */
export type TrainingRun = {
  id: string;
  when: string;
  tried: number;
  kept: readonly string[];
  notKept: readonly string[];
  duration: string | null;
  /** A saved session History still records as running, resting or stopping: not finished, whatever the live status read says. */
  live?: boolean;
  /** The status says it ended but History still records it as live ("Finishing…", or "Stopped · report not saved"):
   * its counts are from the last report saved, not its result. */
  closing?: string;
};
/** A live run that has kept nothing yet has not kept your setup either: it is still deciding. */
const nothingKeptYet = (run: TrainingRun) =>
  (run.id === 'live' || run.live === true) && !run.kept.length;
const LIVE_NOTHING_KEPT = 'Nothing kept yet · still training';
const keptLine = (run: TrainingRun) =>
  run.closing
    ? `${run.closing} · last report: kept ${number(run.kept.length)}, didn’t keep ${number(run.notKept.length)}`
    : nothingKeptYet(run)
      ? `${LIVE_NOTHING_KEPT} · didn’t keep ${number(run.notKept.length)}`
      : `${run.kept.length ? `Kept ${number(run.kept.length)}` : 'Kept your current setup'} · didn’t keep ${number(run.notKept.length)}`;

/** Past runs for Train's advanced details. */
export function TrainingRuns({ runs }: { runs: readonly TrainingRun[] }) {
  if (!runs.length) return null;
  return (
    <section aria-label="Training runs">
      <h4>
        <ShieldCheck size={17} />
        Training runs
      </h4>
      <ul className="lj-run-rows">
        {runs.map((run) => (
          <li key={run.id}>
            <b>{run.when}</b>
            <span>
              {keptLine(run)} · {number(run.tried)} tried
            </span>
            <span>{run.duration ?? ''}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The last few runs, newest first, as History records them: when, what each kept of what it tried
 * (a bar of the two recorded counts, never an estimate), and how long it took. Each opens History. */
export function RecentRuns({
  runs,
  onOpenHistory,
}: {
  runs: readonly TrainingRun[];
  onOpenHistory?: () => void;
}) {
  if (!runs.length) return null;
  return (
    <ol className="lj-recent-runs" aria-label="Training runs">
      {runs.map((run, index) => {
        const kept = run.tried > 0 ? Math.min(1, run.kept.length / run.tried) : 0;
        const body = (
          <>
            <span className="lj-recent-head">
              <b>{run.when}</b>
              {index === 0 && run.id !== 'live' && !run.live && !run.closing && <em>Latest</em>}
              <span>
                {keptLine(run)} · {number(run.tried)} tried
              </span>
              {run.duration && <small>{run.duration}</small>}
            </span>
            <span
              className={`lj-recent-bar ${run.kept.length ? 'has-kept' : ''}`}
              aria-hidden="true"
            >
              <i style={{ width: `${kept * 100}%` }} />
            </span>
            {index > 0 &&
              run.kept.map((line, position) => (
                <span key={position} className="lj-recent-kept">
                  <ShieldCheck size={15} aria-hidden="true" />
                  <span>{line}</span>
                </span>
              ))}
          </>
        );
        return (
          <li key={run.id}>
            {onOpenHistory && run.id !== 'live' ? (
              <button type="button" className="lj-recent-run" onClick={onOpenHistory}>
                {body}
              </button>
            ) : (
              <div className="lj-recent-run">{body}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

const USE_ICON = {
  field: ScanText,
  table: Table2,
  type: Files,
  layout: Files,
  starts: Scissors,
} as const;
export function ResultsTray({
  latest,
  earlier,
  atWork,
  onOpenHistory,
  advanced,
  foot,
}: {
  latest: TrainingRun | null;
  earlier: readonly TrainingRun[];
  atWork: ReturnType<typeof improvementUse>;
  onOpenHistory?: () => void;
  advanced?: ReactNode;
  /** Under both columns: the learning note's ask (shared/email-asks.ts), where people come to see what it learned. */
  foot?: ReactNode;
}) {
  return (
    <>
      <div className="lj-tray-grid lj-results">
        <div className="lj-stat-column">
          <span className="lj-eyebrow">
            {latest ? `${latest.when} · ${number(latest.tried)} tried` : 'Latest training'}
          </span>
          {latest ? (
            <>
              <h3 className="lj-use-title">
                {latest.closing
                  ? latest.closing
                  : latest.kept.length
                    ? `Kept ${plural(latest.kept.length, 'improvement')}`
                    : nothingKeptYet(latest)
                      ? LIVE_NOTHING_KEPT
                      : 'Kept your current setup'}
                {latest.notKept.length > 0 && (
                  <span>Didn’t keep {number(latest.notKept.length)}</span>
                )}
              </h3>
              {latest.kept.length > 0 && (
                <ul className="lj-kept">
                  {latest.kept.map((line, index) => (
                    <li key={index}>
                      <ShieldCheck size={17} aria-hidden="true" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              )}
              {latest.notKept.length > 0 && (
                <>
                  <span className="lj-eyebrow">Didn’t keep, and why</span>
                  <ul className="lj-not-kept">
                    {latest.notKept.map((line, index) => (
                      <li key={index}>
                        <Minus size={15} aria-hidden="true" />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {onOpenHistory && (
                <button type="button" className="lj-link lj-history-link" onClick={onOpenHistory}>
                  See the full run in History
                  <ArrowRight size={14} aria-hidden="true" />
                </button>
              )}
              <div className="lj-room" aria-hidden="true">
                <MilestoneArt kind={5} />
              </div>
            </>
          ) : (
            <div className="lj-empty">
              <MilestoneArt kind={5} />
              <p className="lj-quiet">
                No training runs saved yet. Everyday confirmations still teach this job.
              </p>
            </div>
          )}
        </div>
        <div className="lj-stat-column">
          <span className="lj-eyebrow">Kept changes at work</span>
          {atWork.length ? (
            <>
              <ul className="lj-receipts">
                {atWork.map((entry) => {
                  const Icon = USE_ICON[entry.kind];
                  return (
                    <li key={entry.kind}>
                      <Icon size={17} aria-hidden="true" />
                      <span>
                        <b>{entry.label}</b> {entry.value}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <p className="lj-quiet">Counts documents this job still holds.</p>
            </>
          ) : (
            <p className="lj-quiet">
              No kept change has been used yet. Kept changes show here once documents use them.
            </p>
          )}
          {latest && (
            <>
              <span className="lj-eyebrow">Training runs</span>
              <RecentRuns runs={[latest, ...earlier]} onOpenHistory={onOpenHistory} />
            </>
          )}
          {advanced && <Advanced>{advanced}</Advanced>}
        </div>
      </div>
      {foot && <div className="lj-results-foot">{foot}</div>}
    </>
  );
}
